<template>
  <v-container fluid class="ce-news">
    <v-row class="ce-news__app">
      <v-col cols="12" lg="3" class="ce-news__panel ce-news__panel--filters">
        <div class="ce-news__panel-title">
          <v-icon small>mdi-newspaper-variant-outline</v-icon>
          News
        </div>

        <v-text-field v-model="filtros.titulo" label="Nome da edicao" dense outlined />
        <v-text-field v-model="filtros.editor" label="Editor" dense outlined />

        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="filtros.dataInicio" label="Inicio" type="date" dense outlined />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="filtros.dataFim" label="Fim" type="date" dense outlined />
          </v-col>
        </v-row>

        <v-select
          v-model="filtros.unidadeRegionalId"
          :items="opcoes.unidadesRegionais"
          item-text="nome"
          item-value="id"
          label="Unidade regional"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.municipioId"
          :items="municipiosFiltrados"
          item-text="nome"
          item-value="id"
          label="Municipio"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.statusRelacionamentoId"
          :items="opcoes.status"
          item-text="nome"
          item-value="id"
          label="Status"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.consultorId"
          :items="opcoes.consultores"
          item-text="nome"
          item-value="id"
          label="Consultor"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.eixoId"
          :items="opcoes.eixos"
          item-text="nome"
          item-value="id"
          label="Eixo"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.grupoSolucaoId"
          :items="gruposFiltrados"
          item-text="nome"
          item-value="id"
          label="Grupo de solucao"
          dense
          outlined
          clearable
        />
        <v-select
          v-model="filtros.solucaoId"
          :items="solucoesFiltradas"
          item-text="nome"
          item-value="id"
          label="Solucao"
          dense
          outlined
          clearable
        />

        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model.number="filtros.minimoPaginas" label="Min. paginas" type="number" min="0" dense outlined />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model.number="filtros.maximoPaginas" label="Max. paginas" type="number" min="0" dense outlined />
          </v-col>
        </v-row>

        <v-btn color="primary" block :loading="carregando" @click="gerarJornal">
          <v-icon left>mdi-auto-fix</v-icon>
          Gerar jornal
        </v-btn>
      </v-col>

      <v-col cols="12" lg="6" class="ce-news__paper-wrap">
        <div v-if="!edicao" class="ce-news__empty">
          <v-icon large>mdi-newspaper-variant-multiple-outline</v-icon>
          <strong>Cidade Empreendedora News</strong>
          <span>Escolha os filtros e gere uma primeira edicao.</span>
        </div>

        <div v-else ref="jornal" class="ce-news__paper">
          <div v-if="topTeasers.length" class="ce-news__skyline">
            <a
              v-for="teaser in topTeasers"
              :key="teaser.id"
              class="ce-news__skyline-item"
              :href="teaser.urlPublicaRelatorio || '#'"
              target="_blank"
              rel="noopener"
            >
              <span>{{ teaser.chapeu || teaser.caderno }}</span>
              <strong>{{ teaser.mancheteCurta || teaser.manchete }}</strong>
            </a>
          </div>

          <header class="ce-news__masthead">
            <div class="ce-news__newsmark">
              <div class="ce-news__seal" aria-hidden="true">
                <i v-for="n in 10" :key="n"></i>
              </div>
              <div class="ce-news__newsmark-copy">
                <span>Cidade</span>
                <span>Empreendedora</span>
                <strong>News</strong>
              </div>
            </div>
            <div class="ce-news__title-block">
              <span class="ce-news__kicker">Edicao executiva</span>
              <h1 contenteditable @blur="atualizarCampoEdicao('titulo', $event)">{{ edicao.titulo }}</h1>
            </div>
            <div class="ce-news__edition-meta">
              <img :src="logoSebraeUrl" alt="Sebrae" />
              <span>{{ edicao.dataEdicao }}</span>
              <span>{{ edicao.periodo }}</span>
              <span>{{ edicao.editor }}</span>
            </div>
          </header>

          <section
            v-for="(pagina, paginaIndex) in edicao.paginas"
            :key="pagina.numero"
            class="ce-news__page"
            :class="classePagina(pagina, paginaIndex)"
          >
            <div class="ce-news__page-header">
              <span>Pagina {{ pagina.numero }}</span>
              <input v-model="pagina.nome" />
            </div>

            <div class="ce-news__grid" :class="classeGrid(pagina, paginaIndex)">
              <a
                v-for="(noticia, noticiaIndex) in pagina.noticias"
                :key="noticia.id"
                class="ce-news__story"
                :class="classeNoticia(pagina, paginaIndex, noticiaIndex)"
                :style="estiloNoticia(noticia)"
                :href="noticia.urlPublicaRelatorio || '#'"
                target="_blank"
                rel="noopener"
                @click.prevent="abrirNoticia(noticia, paginaIndex, noticiaIndex)"
              >
                <div
                  v-if="exibirFoto(noticia, paginaIndex, noticiaIndex)"
                  class="ce-news__photo"
                  :class="classeFoto(noticia)"
                  :style="estiloFoto(noticia)"
                >
                  <img
                    v-if="noticia.imagemUrl && !noticia.imagemErro"
                    :src="noticia.imagemUrl"
                    :alt="noticia.imagemLegenda || noticia.manchete"
                    :style="{ objectPosition: noticia.imagemFocoCss || 'center center' }"
                    @error="marcarImagemComErro(noticia)"
                  />
                  <template v-else>
                    <v-icon color="white">mdi-image-area</v-icon>
                    <span>{{ noticia.imagemLegenda || `Imagem editorial de ${noticia.municipio}` }}</span>
                  </template>
                </div>
                <span class="ce-news__section">{{ noticia.chapeu }}</span>
                <span class="ce-news__dateline">{{ noticia.municipio }}</span>
                <h2 contenteditable @blur="atualizarNoticia(noticia, 'manchete', $event)">{{ mancheteExibida(noticia, paginaIndex, noticiaIndex) }}</h2>
                <p class="ce-news__lead" contenteditable @blur="atualizarNoticia(noticia, 'lead', $event)">{{ leadExibido(noticia, paginaIndex, noticiaIndex) }}</p>
                <p class="ce-news__body" contenteditable @blur="atualizarNoticia(noticia, 'corpo', $event)">{{ noticia.corpo }}</p>
                <blockquote contenteditable @blur="atualizarNoticia(noticia, 'destaque', $event)">{{ noticia.destaque || noticia.humor }}</blockquote>
              </a>
            </div>

            <footer class="ce-news__page-footer">
              <span>{{ edicao.totalRelatoriosEncontrados || edicao.totalRelatoriosLidos }} relatorios encontrados</span>
              <span>{{ edicao.totalNoticias }} noticias apuradas</span>
              <span>{{ pagina.caderno || pagina.nome }}</span>
            </footer>
          </section>
        </div>
      </v-col>

      <v-col cols="12" lg="3" class="ce-news__panel ce-news__panel--editor">
        <div class="ce-news__panel-title">
          <v-icon small>mdi-tune-variant</v-icon>
          Edicao
        </div>

        <template v-if="edicao">
          <div class="ce-news__stats">
            <span>{{ edicao.totalNoticias }} noticias</span>
            <span>{{ edicao.totalRelatoriosLidos }} lidas</span>
            <span v-if="edicao.totalRelatoriosEncontrados">{{ edicao.totalRelatoriosEncontrados }} encontradas</span>
          </div>
          <div v-if="edicao.agentesExecutados && edicao.agentesExecutados.length" class="ce-news__agents">
            <span v-for="agente in edicao.agentesExecutados" :key="agente">{{ agente }}</span>
          </div>
          <p class="ce-news__note">{{ edicao.observacoesCuradoria }}</p>

          <v-text-field v-model="edicao.dataEdicao" label="Data da edicao" dense outlined />
          <v-text-field v-model="edicao.periodo" label="Periodo" dense outlined />
          <v-text-field v-model="edicao.editor" label="Editor" dense outlined />

          <div v-if="noticiaSelecionada" class="ce-news__selected">
            <span class="ce-news__selected-label">Noticia selecionada</span>
            <strong>{{ noticiaSelecionada.manchete }}</strong>
            <small>{{ noticiaSelecionada.municipio }} | {{ noticiaSelecionada.caderno }}</small>

            <v-select
              v-model="noticiaSelecionada.caderno"
              :items="cadernos"
              label="Caderno"
              dense
              outlined
            />
            <v-text-field v-model="noticiaSelecionada.municipio" label="Municipio" dense outlined />
            <v-text-field v-model="noticiaSelecionada.consultor" label="Consultor" dense outlined />
            <v-textarea
              v-model="noticiaSelecionada.imagemPrompt"
              label="Briefing da imagem"
              rows="4"
              dense
              outlined
            />
            <v-textarea
              v-model="noticiaSelecionada.textoFonte"
              label="Trecho fonte"
              rows="3"
              dense
              outlined
            />

            <div class="ce-news__actions">
              <v-btn small color="primary" @click="trocarNoticia">
                <v-icon left small>mdi-skip-next</v-icon>
                Proxima
              </v-btn>
              <v-btn small color="warning" @click="usarTituloAlternativo">
                <v-icon left small>mdi-swap-horizontal</v-icon>
                Titulo
              </v-btn>
              <v-btn small color="error" @click="removerNoticia">
                <v-icon left small>mdi-delete</v-icon>
                Remover
              </v-btn>
            </div>
          </div>

          <div class="ce-news__actions ce-news__actions--export">
            <v-btn color="primary" @click="exportarPdf">
              <v-icon left>mdi-file-pdf-box</v-icon>
              Exportar PDF
            </v-btn>
            <v-btn color="secondary" @click="gerarHtml">
              <v-icon left>mdi-link-variant</v-icon>
              Link HTML
            </v-btn>
          </div>

          <v-text-field
            v-if="htmlUrl"
            :value="htmlUrl"
            label="HTML gerado"
            readonly
            dense
            outlined
            append-icon="mdi-open-in-new"
            @click:append="abrirHtml"
          />

          <v-textarea
            v-model="mensagemIa"
            label="Pedido para a IA"
            rows="4"
            outlined
            dense
            placeholder="Ex.: deixe a capa mais alegre e troque a manchete para economia"
          />
          <v-btn block outlined @click="registrarPedidoIa">
            <v-icon left>mdi-robot-outline</v-icon>
            Registrar pedido
          </v-btn>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { atualUfDefinition } from "@/helpers/utils";
import { cidadeEmpreendedoraNewsAPI } from "../API/CidadeEmpreendedoraNewsAPI";

export default {
  name: "CidadeEmpreendedoraNewsEditor",
  data() {
    return {
      carregando: false,
      filtros: {
        titulo: "Cidade Empreendedora News",
        editor: "",
        dataInicio: "2026-05-01",
        dataFim: "2026-05-31",
        unidadeRegionalId: null,
        municipioId: null,
        statusRelacionamentoId: null,
        consultorId: null,
        eixoId: null,
        grupoSolucaoId: null,
        solucaoId: null,
        minimoPaginas: 0,
        maximoPaginas: 6,
      },
      opcoes: {
        unidadesRegionais: [],
        municipios: [],
        consultores: [],
        eixos: [],
        gruposSolucoes: [],
        solucoes: [],
        status: [],
      },
      edicao: null,
      noticiaAtiva: { pagina: 0, noticia: 0 },
      htmlUrl: "",
      mensagemIa: "",
    };
  },
  computed: {
    municipiosFiltrados() {
      if (!this.filtros.unidadeRegionalId) return this.opcoes.municipios;
      return this.opcoes.municipios.filter(
        (m) => m.unidadeRegionalId === this.filtros.unidadeRegionalId
      );
    },
    gruposFiltrados() {
      if (!this.filtros.eixoId) return this.opcoes.gruposSolucoes;
      return this.opcoes.gruposSolucoes.filter((g) => g.eixoId === this.filtros.eixoId);
    },
    solucoesFiltradas() {
      return this.opcoes.solucoes.filter((s) => {
        if (this.filtros.eixoId && s.eixoId !== this.filtros.eixoId) return false;
        if (this.filtros.grupoSolucaoId && s.grupoSolucaoId !== this.filtros.grupoSolucaoId) return false;
        return true;
      });
    },
    noticiaSelecionada() {
      if (!this.edicao || !this.edicao.paginas[this.noticiaAtiva.pagina]) return null;
      return this.edicao.paginas[this.noticiaAtiva.pagina].noticias[this.noticiaAtiva.noticia] || null;
    },
    cadernos() {
      if (!this.edicao) return [];
      return Array.from(new Set(this.edicao.noticias.map((n) => n.caderno))).sort();
    },
    topTeasers() {
      if (!this.edicao) return [];
      const usadasNaCapa = new Set(
        ((this.edicao.paginas && this.edicao.paginas[0] && this.edicao.paginas[0].noticias) || []).map((n) => n.id)
      );
      return (this.edicao.noticias || [])
        .filter((n) => !usadasNaCapa.has(n.id))
        .slice(0, 3);
    },
    logoCidadeUrl() {
      return new URL("Logos/Logo_ce.jpg", atualUfDefinition.env).toString();
    },
    logoSebraeUrl() {
      return "/assets/img/sebrae-logo.png";
    },
  },
  mounted() {
    if (!localStorage.getItem("token")) return;
    this.carregarFiltros();
  },
  methods: {
    async carregarFiltros() {
      const { data } = await cidadeEmpreendedoraNewsAPI.filtros();
      this.opcoes = data;
    },
    async gerarJornal() {
      this.carregando = true;
      this.htmlUrl = "";
      try {
        const { data } = await cidadeEmpreendedoraNewsAPI.gerar(this.filtros);
        this.edicao = this.prepararEdicao(data);
        this.noticiaAtiva = { pagina: 0, noticia: 0 };
      } finally {
        this.carregando = false;
      }
    },
    prepararEdicao(edicao) {
      const noticiasPorId = new Map(
        (edicao.noticias || []).map((noticia) => {
          const preparada = this.prepararNoticia(noticia);
          return [preparada.id, preparada];
        })
      );

      return {
        ...edicao,
        noticias: Array.from(noticiasPorId.values()),
        paginas: (edicao.paginas || []).map((pagina) => ({
          ...pagina,
          noticias: (pagina.noticias || []).map((noticia) => {
            const existente = noticiasPorId.get(noticia.id);
            return existente ? JSON.parse(JSON.stringify(existente)) : this.prepararNoticia(noticia);
          }),
        })),
      };
    },
    prepararNoticia(noticia) {
      return {
        ...noticia,
        destaque: noticia.destaque || noticia.humor || "",
        imagemErro: false,
        imagemTipo: noticia.imagemGeradaDataUrl && noticia.tratamentoFoto === "ilustracao-editorial" ? "gerada" : (noticia.imagemAnexoId ? "real" : noticia.imagemTipo),
        papelEditorial: noticia.papelEditorial || "wide",
        classeDiagramacao: noticia.classeDiagramacao || noticia.papelEditorial || "wide",
        mostrarFoto: noticia.mostrarFoto !== false,
        imagemRelevante: noticia.imagemRelevante !== false,
        tratamentoFoto: noticia.tratamentoFoto || "",
        imagemUrl: noticia.imagemGeradaDataUrl && noticia.tratamentoFoto === "ilustracao-editorial"
          ? noticia.imagemGeradaDataUrl
          : noticia.imagemAnexoId
          ? this.montarUrlAnexo(noticia.imagemAnexoId)
          : (noticia.imagemGeradaDataUrl || ""),
      };
    },
    montarUrlAnexo(anexoId) {
      const base = String(atualUfDefinition.env || "").replace(/\/?$/, "/");
      return `${base}api/arquivo/anexo/visualizar/${anexoId}?banco=${encodeURIComponent(atualUfDefinition.banco)}`;
    },
    marcarImagemComErro(noticia) {
      this.$set(noticia, "imagemErro", true);
    },
    selecionarNoticia(pagina, noticia) {
      this.noticiaAtiva = { pagina, noticia };
    },
    abrirNoticia(noticia, pagina, noticiaIndex) {
      this.selecionarNoticia(pagina, noticiaIndex);
      if (noticia && noticia.urlPublicaRelatorio) {
        window.open(noticia.urlPublicaRelatorio, "_blank", "noopener");
      }
    },
    mancheteExibida(noticia, paginaIndex, noticiaIndex) {
      const papel = this.papelEditorial(noticia, paginaIndex, noticiaIndex);
      const texto = ["brief", "note", "sidebar", "banner", "strip"].includes(papel) || (paginaIndex === 0 && noticiaIndex > 0)
        ? noticia.mancheteCurta || noticia.manchete
        : noticia.manchete || noticia.mancheteCurta;
      const limites = { main: 58, lead: 44, wide: 40, strip: 34, banner: 34, photo: 30, tall: 30, brief: 28, note: 28, sidebar: 28 };
      return this.limitarTextoEditorial(texto, limites[papel] || 38);
    },
    leadExibido(noticia, paginaIndex, noticiaIndex) {
      const papel = this.papelEditorial(noticia, paginaIndex, noticiaIndex);
      const texto = ["brief", "note", "sidebar", "banner", "strip"].includes(papel) || (paginaIndex === 0 && noticiaIndex > 0) || (paginaIndex > 0 && noticiaIndex > 0)
        ? noticia.leadCurto || noticia.lead
        : noticia.lead || noticia.leadCurto;
      const limites = { main: 130, lead: 82, brief: 58, note: 58, sidebar: 58 };
      return this.limitarTextoEditorial(texto, limites[papel] || 68);
    },
    limitarTextoEditorial(valor, maximo) {
      let texto = String(valor || "").replace(/\s+/g, " ").trim();
      if (maximo && texto.length > maximo) {
        const corte = texto.lastIndexOf(" ", maximo);
        texto = texto.slice(0, corte > 20 ? corte : maximo).trim().replace(/[.,;:-]+$/, "");
      }
      const fracas = ["a", "as", "o", "os", "e", "em", "de", "da", "das", "do", "dos", "para", "com", "por", "no", "nos", "na", "nas"];
      while (texto.includes(" ") && fracas.includes(texto.split(" ").pop().toLowerCase())) {
        texto = texto.slice(0, texto.lastIndexOf(" ")).trim().replace(/[.,;:-]+$/, "");
      }
      return texto;
    },
    classePagina(pagina, paginaIndex) {
      const densidade = String(pagina.densidadeEditorial || "variavel").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      return {
        "ce-news__page--cover": paginaIndex === 0,
        [`ce-news__page--${pagina.layout || "caderno"}`]: true,
        [`ce-news__page--template-${pagina.templateDiagramacao || "mosaico-editorial"}`]: true,
        [`ce-news__page--density-${densidade}`]: true,
      };
    },
    classeGrid(pagina, paginaIndex) {
      const densidade = String(pagina.densidadeEditorial || "variavel").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      return {
        "ce-news__grid--cover": paginaIndex === 0,
        [`ce-news__grid--${pagina.layout || "caderno"}`]: true,
        [`ce-news__grid--template-${pagina.templateDiagramacao || "mosaico-editorial"}`]: true,
        [`ce-news__grid--density-${densidade}`]: true,
        [`ce-news__grid--count-${(pagina.noticias || []).length}`]: true,
      };
    },
    classeNoticia(pagina, paginaIndex, noticiaIndex) {
      const noticia = pagina.noticias[noticiaIndex] || {};
      const papel = this.papelEditorial(noticia, paginaIndex, noticiaIndex);
      const classe = noticia.classeDiagramacao || papel;
      return {
        [`ce-news__story--${papel}`]: true,
        [`ce-news__story--${classe}`]: true,
        [`ce-news__story--slot-${noticiaIndex}`]: true,
        [`ce-news__story--photo-${noticia.tratamentoFoto || "auto"}`]: true,
        "ce-news__story--side": ["note", "brief", "sidebar", "tall"].includes(papel),
        "ce-news__story--compact": ["note", "brief", "sidebar", "tall", "wide", "strip", "banner"].includes(papel),
        [`ce-news__story--layout-${pagina.layout || "caderno"}`]: true,
      };
    },
    papelEditorial(noticia, paginaIndex, noticiaIndex) {
      if (noticia && noticia.papelEditorial) return noticia.papelEditorial;
      if (paginaIndex === 0) {
        if (noticiaIndex === 0) return "main";
        if (noticiaIndex === 1) return "lead";
        if (noticiaIndex === 2 || noticiaIndex === 3) return "tall";
        if ([4, 5, 6, 9].includes(noticiaIndex)) return "wide";
        return "note";
      }
      if (noticiaIndex === 0) return "lead";
      if (noticiaIndex >= 8) return "note";
      return "wide";
    },
    exibirFoto(noticia, paginaIndex, noticiaIndex) {
      const papel = this.papelEditorial(noticia, paginaIndex, noticiaIndex);
      if (["note", "brief", "sidebar"].includes(papel)) return false;
      if (noticia.mostrarFoto === false || noticia.imagemRelevante === false) return false;
      return Boolean(noticia.imagemUrl && !noticia.imagemErro);
    },
    estiloNoticia(noticia) {
      const style = {};
      if (this.gridValido(noticia.gridColumn)) style.gridColumn = noticia.gridColumn;
      if (this.gridValido(noticia.gridRow)) style.gridRow = noticia.gridRow;
      return style;
    },
    estiloFoto(noticia) {
      const style = {};
      if (/^\d{2,3}px$/.test(String(noticia.alturaFoto || ""))) style.height = noticia.alturaFoto;
      return style;
    },
    gridValido(valor) {
      return /^\d{1,2}\s*\/\s*\d{1,2}$/.test(String(valor || ""));
    },
    classeFoto(noticia) {
      return {
        "ce-news__photo--real": noticia.imagemTipo === "real" && noticia.imagemUrl && !noticia.imagemErro,
        "ce-news__photo--documental": noticia.tratamentoFoto === "apoio-documental",
        "ce-news__photo--generated": (noticia.imagemTipo === "gerada" || noticia.imagemTipo === "editorial") && noticia.imagemUrl && !noticia.imagemErro,
      };
    },
    atualizarCampoEdicao(campo, event) {
      this.edicao[campo] = event.target.innerText.trim();
    },
    atualizarNoticia(noticia, campo, event) {
      noticia[campo] = event.target.innerText.trim();
    },
    trocarNoticia() {
      const pagina = this.edicao.paginas[this.noticiaAtiva.pagina];
      if (!pagina) return;
      const atual = pagina.noticias[this.noticiaAtiva.noticia];
      const usados = new Set(this.edicao.paginas.flatMap((p) => p.noticias.map((n) => n.id)));
      const proxima = this.edicao.noticias.find((n) => !usados.has(n.id) && n.id !== atual.id)
        || this.edicao.noticias.find((n) => n.id !== atual.id);
      if (proxima) {
        this.$set(pagina.noticias, this.noticiaAtiva.noticia, JSON.parse(JSON.stringify(this.prepararNoticia(proxima))));
      }
    },
    usarTituloAlternativo() {
      if (!this.noticiaSelecionada) return;
      const atual = this.noticiaSelecionada.manchete;
      this.noticiaSelecionada.manchete = this.noticiaSelecionada.tituloAlternativo || atual;
      this.noticiaSelecionada.tituloAlternativo = atual;
    },
    removerNoticia() {
      const pagina = this.edicao.paginas[this.noticiaAtiva.pagina];
      if (!pagina) return;
      pagina.noticias.splice(this.noticiaAtiva.noticia, 1);
      this.noticiaAtiva.noticia = Math.max(0, this.noticiaAtiva.noticia - 1);
    },
    exportarPdf() {
      window.print();
    },
    gerarHtml() {
      if (this.htmlUrl) URL.revokeObjectURL(this.htmlUrl);
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${this.escapeHtml(this.edicao.titulo)}</title>${this.cssExportacao()}</head><body>${this.$refs.jornal.outerHTML}</body></html>`;
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      this.htmlUrl = URL.createObjectURL(blob);
    },
    abrirHtml() {
      if (this.htmlUrl) window.open(this.htmlUrl, "_blank");
    },
    registrarPedidoIa() {
      if (!this.mensagemIa.trim() || !this.edicao) return;
      this.edicao.observacoesCuradoria = `${this.edicao.observacoesCuradoria}\nPedido editorial: ${this.mensagemIa.trim()}`;
      this.mensagemIa = "";
    },
    iniciais(nome) {
      return String(nome || "CE")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((parte) => parte.charAt(0))
        .join("")
        .toUpperCase();
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    cssExportacao() {
      return `<style>${Array.from(document.styleSheets).map((sheet) => {
        try {
          return Array.from(sheet.cssRules || []).map((rule) => rule.cssText).join("\\n");
        } catch (e) {
          return "";
        }
      }).join("\\n")}</style>`;
    },
  },
};
</script>

<style scoped>
.ce-news {
  background: #eef4f7;
  color: #102c3f;
  min-height: calc(100vh - 70px);
}

.ce-news__app {
  align-items: stretch;
}

.ce-news__panel {
  background: #ffffff;
  border-right: 1px solid #d8dedf;
  min-height: calc(100vh - 88px);
  overflow-y: auto;
}

.ce-news__panel--editor {
  border-left: 1px solid #d8dedf;
  border-right: 0;
}

.ce-news__panel-title {
  align-items: center;
  border-bottom: 1px solid #d8dedf;
  display: flex;
  font-size: 16px;
  font-weight: 700;
  gap: 8px;
  margin: -12px -12px 16px;
  padding: 14px 12px;
}

.ce-news__paper-wrap {
  max-height: calc(100vh - 88px);
  overflow: auto;
  padding: 18px;
}

.ce-news__empty {
  align-items: center;
  background: #ffffff;
  border: 1px dashed #9ca7aa;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 420px;
}

.ce-news__paper {
  background: #ffffff;
  border-top: 12px solid #005eb8;
  box-shadow: 0 16px 40px rgba(0, 61, 115, 0.18);
  margin: 0 auto;
  max-width: 1020px;
  padding: 16px 20px 24px;
}

.ce-news__skyline {
  border-bottom: 4px solid #102c3f;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 8px;
}

.ce-news__skyline-item {
  background: #d21f26;
  color: #ffffff;
  min-height: 78px;
  padding: 8px 10px;
  text-decoration: none;
}

.ce-news__skyline-item:nth-child(2) {
  background: #005eb8;
}

.ce-news__skyline-item:nth-child(3) {
  background: #00a859;
}

.ce-news__skyline-item span {
  display: block;
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.ce-news__skyline-item strong {
  display: -webkit-box;
  font-family: Arial, sans-serif;
  font-size: 17px;
  line-height: 1.04;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.ce-news__masthead {
  align-items: center;
  background:
    linear-gradient(90deg, rgba(0, 94, 184, 0.08), rgba(255, 255, 255, 0.92)),
    #ffffff;
  border-bottom: 2px solid #102c3f;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0 12px;
}

.ce-news__newsmark {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 270px;
}

.ce-news__seal {
  background: #005eb8;
  border-radius: 50%;
  box-shadow: inset 0 0 0 8px #ffffff, 0 7px 18px rgba(0, 94, 184, 0.24);
  height: 58px;
  position: relative;
  width: 58px;
}

.ce-news__seal i {
  background: #f5b400;
  border-radius: 8px 8px 2px 2px;
  height: 23px;
  left: 27px;
  position: absolute;
  top: 4px;
  transform-origin: 2px 25px;
  width: 5px;
}

.ce-news__seal i:nth-child(1) { transform: rotate(0deg); }
.ce-news__seal i:nth-child(2) { transform: rotate(36deg); }
.ce-news__seal i:nth-child(3) { transform: rotate(72deg); }
.ce-news__seal i:nth-child(4) { transform: rotate(108deg); }
.ce-news__seal i:nth-child(5) { transform: rotate(144deg); }
.ce-news__seal i:nth-child(6) { transform: rotate(180deg); }
.ce-news__seal i:nth-child(7) { transform: rotate(216deg); }
.ce-news__seal i:nth-child(8) { transform: rotate(252deg); }
.ce-news__seal i:nth-child(9) { transform: rotate(288deg); }
.ce-news__seal i:nth-child(10) { transform: rotate(324deg); }

.ce-news__seal::after {
  background: #00a859;
  border: 4px solid #ffffff;
  border-radius: 50%;
  content: "";
  height: 20px;
  left: 19px;
  position: absolute;
  top: 19px;
  width: 20px;
}

.ce-news__newsmark-copy {
  display: flex;
  flex-direction: column;
  font-family: Arial, Helvetica, sans-serif;
  line-height: 0.86;
  text-transform: uppercase;
}

.ce-news__newsmark span {
  color: #005eb8;
  font-size: 13px;
  font-weight: 900;
}

.ce-news__newsmark strong {
  color: #102c3f;
  font-size: 42px;
  font-weight: 900;
}

.ce-news__title-block {
  flex: 1;
  min-width: 0;
}

.ce-news__masthead h1 {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 38px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 0.94;
  margin: 0;
}

.ce-news__kicker,
.ce-news__section,
.ce-news__selected-label {
  color: #f58220;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.ce-news__dateline {
  color: #385061;
  display: block;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.05;
  margin-top: 3px;
}

.ce-news__edition-meta {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 700;
  gap: 3px;
  text-align: right;
}

.ce-news__edition-meta img {
  align-self: flex-end;
  max-height: 34px;
  max-width: 88px;
  object-fit: contain;
}

.ce-news__page {
  border-bottom: 2px solid #102c3f;
  min-height: 1080px;
  padding: 12px 0 28px;
  page-break-after: always;
  position: relative;
}

.ce-news__page-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ce-news__page-header span {
  background: transparent;
  border-bottom: 3px solid #d21f26;
  color: #102c3f;
  font-size: 11px;
  font-weight: 800;
  padding: 2px 0 4px;
  text-transform: uppercase;
}

.ce-news__page-header input {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #90999c;
  font-weight: 800;
  max-width: 220px;
  text-align: right;
}

.ce-news__grid {
  display: grid;
  gap: 8px 9px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: repeat(12, minmax(42px, 1fr));
  min-height: 860px;
}

.ce-news__page:not(.ce-news__page--cover) .ce-news__grid {
  min-height: 960px;
}

.ce-news__grid--density-texto-denso {
  gap: 4px 5px;
  grid-template-rows: repeat(12, minmax(40px, 1fr));
}

.ce-news__grid--density-fluxo-jornal,
.ce-news__grid--template-colunas-fluidas {
  column-count: 3;
  column-gap: 12px;
  display: block;
}

.ce-news__story {
  border-top: 1px solid #b7c3c9;
  box-shadow: inset 0 -1px 0 #d7e0e6;
  color: #102c3f;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 7px 6px 6px;
  text-decoration: none;
}

.ce-news__story--brief,
.ce-news__story--note,
.ce-news__story--sidebar {
  background: #f4f8f7;
  border-top: 4px solid #7db7aa;
  padding: 6px 7px;
}

.ce-news__story--banner {
  background: #eef6fb;
  border-top: 4px solid #005eb8;
}

.ce-news__story--main {
  grid-column: 1 / 8;
  grid-row: 1 / 6;
}

.ce-news__story--lead {
  grid-column: 8 / 13;
  grid-row: 1 / 4;
}

.ce-news__story--slot-2 {
  grid-column: 8 / 10;
  grid-row: 4 / 6;
}

.ce-news__story--slot-3 {
  grid-column: 10 / 13;
  grid-row: 4 / 6;
}

.ce-news__story--slot-4 {
  grid-column: 1 / 5;
  grid-row: 6 / 8;
}

.ce-news__story--slot-5 {
  grid-column: 5 / 9;
  grid-row: 6 / 8;
}

.ce-news__story--slot-6 {
  grid-column: 9 / 13;
  grid-row: 6 / 9;
}

.ce-news__story--slot-7 {
  grid-column: 1 / 3;
  grid-row: 8 / 10;
}

.ce-news__story--slot-8 {
  grid-column: 3 / 5;
  grid-row: 8 / 10;
}

.ce-news__story--slot-9 {
  grid-column: 5 / 9;
  grid-row: 8 / 11;
}

.ce-news__story--slot-10 {
  grid-column: 9 / 11;
  grid-row: 9 / 11;
}

.ce-news__story--slot-11 {
  grid-column: 11 / 13;
  grid-row: 9 / 11;
}

.ce-news__story h2 {
  display: block;
  display: -webkit-box;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 17px;
  font-weight: 900;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  letter-spacing: 0;
  line-height: 1.04;
  margin: 5px 0 6px;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.ce-news__story--main h2 {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 27px;
  line-height: 1.05;
}

.ce-news__story--lead h2 {
  font-size: 21px;
  line-height: 1.04;
}

.ce-news__story--wide h2 {
  font-size: 17px;
  line-height: 1.04;
}

.ce-news__story--banner h2 {
  font-size: 17px;
  line-height: 1.04;
}

.ce-news__story--strip h2 {
  font-size: 17px;
}

.ce-news__story--sidebar h2,
.ce-news__story--brief h2 {
  font-size: 13px;
  line-height: 1.02;
  -webkit-line-clamp: 4;
}

.ce-news__story--photo h2 {
  font-size: 13px;
  line-height: 1.02;
}

.ce-news__story--tall h2,
.ce-news__story--note h2 {
  font-size: 11px;
}

.ce-news__story p {
  font-size: 12px;
  line-height: 1.32;
  margin: 6px 0;
}

.ce-news__lead {
  display: -webkit-box;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px !important;
  font-weight: 700;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.18;
  overflow: hidden;
}

.ce-news__body {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.ce-news__story--compact .ce-news__body {
  -webkit-line-clamp: 3;
}

.ce-news__page--cover .ce-news__story--side .ce-news__photo {
  height: 78px;
}

.ce-news__page--cover .ce-news__story--side h2 {
  font-size: 16px;
}

.ce-news__page--cover .ce-news__story--side .ce-news__lead {
  font-size: 10px !important;
  line-height: 1.14;
}

.ce-news__page--cover .ce-news__story--side blockquote {
  display: none;
}

.ce-news__story--slot-0 .ce-news__photo {
  height: 250px;
}

.ce-news__story--slot-1 .ce-news__photo {
  height: 142px;
}

.ce-news__story--slot-4 .ce-news__photo,
.ce-news__story--slot-5 .ce-news__photo {
  height: 84px;
}

.ce-news__story--slot-6 .ce-news__photo,
.ce-news__story--slot-9 .ce-news__photo {
  height: 138px;
}

.ce-news__story--note blockquote,
.ce-news__story--brief blockquote,
.ce-news__story--sidebar blockquote,
.ce-news__story--strip blockquote,
.ce-news__story--banner blockquote,
.ce-news__story--tall blockquote,
.ce-news__story--wide blockquote {
  display: none;
}

.ce-news__story--wide .ce-news__body,
.ce-news__story--strip .ce-news__body,
.ce-news__story--banner .ce-news__body,
.ce-news__story--tall .ce-news__body,
.ce-news__story--note .ce-news__body,
.ce-news__story--brief .ce-news__body,
.ce-news__story--sidebar .ce-news__body {
  -webkit-line-clamp: 2;
  font-size: 10px;
  line-height: 1.18;
  margin-top: 4px;
}

.ce-news__story--note .ce-news__body,
.ce-news__story--brief .ce-news__body,
.ce-news__story--sidebar .ce-news__body {
  -webkit-line-clamp: 6;
}

.ce-news__story--note .ce-news__lead,
.ce-news__story--brief .ce-news__lead,
.ce-news__story--sidebar .ce-news__lead {
  font-size: 10px !important;
  line-height: 1.18;
  margin-top: 3px;
}

.ce-news__page--density-texto-denso .ce-news__story {
  background: #ffffff;
  box-shadow: inset -1px 0 #d7e0e6, inset 0 -1px 0 #d7e0e6;
  padding: 6px 7px;
}

.ce-news__page--density-texto-denso .ce-news__story h2 {
  font-size: 13px;
  -webkit-line-clamp: 3;
  line-height: 1.02;
  margin: 3px 0 4px;
}

.ce-news__page--density-texto-denso .ce-news__story--wide h2,
.ce-news__page--density-texto-denso .ce-news__story--banner h2 {
  font-size: 16px;
}

.ce-news__page--density-texto-denso .ce-news__lead {
  font-size: 10px !important;
  line-height: 1.12;
}

.ce-news__page--density-texto-denso .ce-news__body {
  -webkit-line-clamp: 5;
  font-size: 10px;
  line-height: 1.14;
  margin-top: 4px;
}

.ce-news__page--density-fluxo-jornal .ce-news__story,
.ce-news__page--template-colunas-fluidas .ce-news__story {
  break-inside: avoid;
  display: inline-block;
  height: auto;
  margin: 0 0 10px;
  min-height: 0;
  padding: 7px 8px;
  page-break-inside: avoid;
  width: 100%;
}

.ce-news__page--density-fluxo-jornal .ce-news__story h2,
.ce-news__page--template-colunas-fluidas .ce-news__story h2 {
  -webkit-line-clamp: 4;
  font-size: 17px;
  line-height: 1.02;
}

.ce-news__page--density-fluxo-jornal .ce-news__story--main h2,
.ce-news__page--density-fluxo-jornal .ce-news__story--lead h2,
.ce-news__page--template-colunas-fluidas .ce-news__story--main h2,
.ce-news__page--template-colunas-fluidas .ce-news__story--lead h2 {
  font-size: 23px;
}

.ce-news__page--density-fluxo-jornal .ce-news__lead,
.ce-news__page--template-colunas-fluidas .ce-news__lead {
  -webkit-line-clamp: 3;
}

.ce-news__page--density-fluxo-jornal .ce-news__body,
.ce-news__page--template-colunas-fluidas .ce-news__body {
  -webkit-line-clamp: 8;
}

.ce-news__story--tall .ce-news__lead,
.ce-news__story--strip .ce-news__lead,
.ce-news__story--banner .ce-news__lead {
  font-size: 11px !important;
  line-height: 1.16;
}

.ce-news__photo {
  align-items: center;
  background:
    linear-gradient(135deg, rgba(0, 94, 184, 0.9), rgba(0, 168, 89, 0.82)),
    url("/assets/img/mapa/cidade-base.jpg") center/cover;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  height: 120px;
  justify-content: center;
  margin-bottom: 10px;
  padding: 14px;
  text-align: center;
}

.ce-news__photo img {
  display: block;
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.ce-news__photo--real {
  background: #edf4f7;
  display: block;
  padding: 0;
}

.ce-news__photo--real img {
  object-fit: cover;
}

.ce-news__photo--documental img {
  object-fit: contain;
}

.ce-news__photo--generated {
  background: #dce8ee;
  display: block;
  padding: 0;
}

.ce-news__photo--generated img {
  object-fit: cover;
}

.ce-news__story--main .ce-news__photo {
  height: 255px;
}

.ce-news__story--lead .ce-news__photo {
  height: 210px;
}

.ce-news__story--photo .ce-news__photo {
  height: 190px;
}

.ce-news__story--strip .ce-news__photo,
.ce-news__story--banner .ce-news__photo {
  height: 92px;
}

.ce-news__story--tall .ce-news__photo {
  height: 230px;
}

.ce-news__grid--compacto .ce-news__story--lead .ce-news__photo {
  height: 124px;
}

.ce-news__story blockquote {
  border-left: 4px solid #f58220;
  font-family: Georgia, "Times New Roman", serif;
  display: -webkit-box;
  font-size: 13px;
  font-style: italic;
  margin: 10px 0;
  overflow: hidden;
  padding-left: 10px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.ce-news__story--side blockquote,
.ce-news__story--compact blockquote {
  -webkit-line-clamp: 2;
}

.ce-news__tags,
.ce-news__stats,
.ce-news__agents,
.ce-news__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ce-news__tags span,
.ce-news__stats span,
.ce-news__agents span {
  background: #e8f4fb;
  border-radius: 4px;
  color: #005eb8;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 6px;
}

.ce-news__page-footer {
  align-items: center;
  border-top: 1px solid #b7c3c9;
  bottom: 0;
  color: #5d6b73;
  display: flex;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10px;
  font-weight: 700;
  gap: 10px;
  justify-content: space-between;
  left: 0;
  padding-top: 6px;
  position: absolute;
  right: 0;
  text-transform: uppercase;
}

.ce-news__agents {
  margin-top: 8px;
}

.ce-news__agents span {
  background: #f7efe7;
  color: #9b4b00;
}

.ce-news__note {
  background: #f2f9f5;
  border-left: 4px solid #00a859;
  font-size: 12px;
  margin: 14px 0;
  padding: 10px;
  white-space: pre-line;
}

.ce-news__selected {
  border: 1px solid #d8dedf;
  margin: 12px 0;
  padding: 12px;
}

.ce-news__selected strong,
.ce-news__selected small {
  display: block;
  margin-bottom: 8px;
}

.ce-news__actions--export {
  margin: 16px 0;
}

@media print {
  body * {
    visibility: hidden;
  }

  .ce-news__paper,
  .ce-news__paper * {
    visibility: visible;
  }

  .ce-news__paper {
    box-shadow: none;
    left: 0;
    max-width: none;
    padding: 8mm;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .ce-news__page {
    break-after: page;
    min-height: auto;
  }
}

@media (max-width: 960px) {
  .ce-news__panel,
  .ce-news__paper-wrap {
    max-height: none;
    min-height: auto;
  }

  .ce-news__masthead,
  .ce-news__grid,
  .ce-news__grid--cover {
    display: block;
  }

  .ce-news__masthead h1 {
    font-size: 34px;
  }

  .ce-news__edition-meta {
    margin-top: 10px;
    text-align: left;
  }
}
</style>
